import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import BrandLogo from "@/components/BrandLogo";
import { KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ForcePasswordChange() {
    const { user, refresh, logout } = useAuth();
    const nav = useNavigate();
    const [cur, setCur] = useState("");
    const [np, setNp] = useState("");
    const [cp, setCp] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    if (!user) {
        nav("/login", { replace: true });
        return null;
    }

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (np.length < 6) return setErr("كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل");
        if (np !== cp) return setErr("كلمة المرور وتأكيدها غير متطابقين");
        setBusy(true);
        try {
            await api.post("/auth/change-password", { current_password: cur, new_password: np });
            toast.success("تم تحديث كلمة المرور");
            await refresh();
            const roleTo = { admin: "/admin", trainer: "/trainer", student: "/student", parent: "/parent" };
            nav(roleTo[user.role] || "/", { replace: true });
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div dir="rtl" className="min-h-screen brand-backdrop flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <div className="mb-6 flex justify-center">
                    <BrandLogo size={56} textClass="text-white" />
                </div>
                <div className="bg-white rounded-2xl shadow-2xl p-8">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-11 h-11 rounded-lg bg-[#FFF1F2] flex items-center justify-center">
                            <KeyRound className="w-5 h-5 text-[#C8102E]" />
                        </div>
                        <div>
                            <h1 className="text-lg font-bold text-[#0F172A]">تغيير كلمة المرور</h1>
                            <p className="text-xs text-slate-500">هذا أول تسجيل دخول، الرجاء تعيين كلمة مرور جديدة</p>
                        </div>
                    </div>

                    <form onSubmit={submit} className="space-y-4" data-testid="force-change-form">
                        <div>
                            <Label>كلمة المرور الحالية</Label>
                            <Input data-testid="current-password-input" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1.5 h-11" />
                        </div>
                        <div>
                            <Label>كلمة المرور الجديدة</Label>
                            <Input data-testid="new-password-input" type="password" value={np} onChange={(e) => setNp(e.target.value)} className="mt-1.5 h-11" />
                        </div>
                        <div>
                            <Label>تأكيد كلمة المرور</Label>
                            <Input data-testid="confirm-password-input" type="password" value={cp} onChange={(e) => setCp(e.target.value)} className="mt-1.5 h-11" />
                        </div>
                        {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
                        <Button data-testid="save-password-button" type="submit" disabled={busy} className="w-full h-11 bg-[#1B2A4E] hover:bg-[#0F172A]">
                            {busy ? "جارٍ الحفظ..." : "حفظ كلمة المرور"}
                        </Button>
                        <button type="button" onClick={async () => { await logout(); nav("/login", { replace: true }); }} className="w-full text-xs text-slate-400 hover:text-slate-600">
                            الخروج
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
